/* eslint-disable react/jsx-no-bind */
import React from 'react';
import styled from 'styled-components';
import { v4 as uuidv4 } from 'uuid';
import { rem } from 'polished';
import { BreakpointContext, useBreakpoints } from 'react-use-breakpoints';
import { Link } from 'gatsby';
import { faChevronRight } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Slider } from '../Slider';
import { ServiceThumbnail } from '../ServiceThumbnail';
import { Service } from './types';
import { SimpleModal } from '../SimpleModal';
import { serviceMock } from './mocks';
import { Button } from '../Button';
import { ServiceThumbnailsGrid } from '../ServiceThumbnailsGrid';

interface IServicesSlider {
  className?: string;
  services: Service[];
  slider?: boolean;
}

export const StyledServicesSlider = styled.div`
  width: 100%;
  .service-slide {
    padding: 0 ${rem(`6px`)};
    ${({ theme }) => theme.breakpoints.phone`
      padding: 0 ${rem(`16px`)};
    `}
  }
  .slick-slider {
    padding: 0;
    .slick-list {
      margin: 0 ${rem(`-16px`)};
      padding: 0;
      ${({ theme }) => theme.breakpoints.phone`
        margin: 0 ${rem(`-16px`)};
      `}
    }
  }
  .slick-track {
    padding: 0 0 ${rem(`24px`)};
    ${({ theme }) => theme.breakpoints.phablet`
      padding: 0 0 ${rem(`32px`)};
    `}
    ${({ theme }) => theme.breakpoints.desktop`
      padding: ${rem(`36px`)} 0 ${rem(`32px`)};
    `}
  }
`;

export const ServicesSlider = ({
  services,
  className,
  slider = true,
}: IServicesSlider) => {
  const breakpoints = React.useContext(BreakpointContext);
  const { breakpoint } = useBreakpoints(breakpoints);
  const isUnderTablet = [`watch`, `phone`, `phablet`, `tablet`].some(
    (item) => item === breakpoint,
  );
  const isUnderPhablet = [`watch`, `phone`, `phablet`].some(
    (item) => item === breakpoint,
  );
  const [modalIsOpen, setIsOpen] = React.useState(false);
  const [selectedService, setSelectedService] =
    React.useState<Service>(serviceMock);

  function openModal() {
    setIsOpen(true);
  }

  function closeModal() {
    setIsOpen(false);
  }

  const settingsSlider = {
    slidesToShow: 4,
    slidesToScroll: 1,
    infinite: false,
  };

  const settingsSliderPhoneResponsive = {
    slidesToShow: 1,
    slidesToScroll: 1,
    centerMode: true,
    centerPadding: `20px`,
  };

  const settingsSliderPhabletResponsive = {
    slidesToShow: 2,
    slidesToScroll: 1,
  };

  const settingsSliderTabletResponsive = {
    slidesToShow: 3,
    slidesToScroll: 1,
  };

  return (
    <StyledServicesSlider className={className}>
      {slider && isUnderPhablet ? (
        <Slider
          className="services-slider"
          settings={settingsSlider}
          responsivePhoneSettings={settingsSliderPhoneResponsive}
          responsivePhabletSettings={settingsSliderPhabletResponsive}
          responsiveTabletSettings={settingsSliderTabletResponsive}
          paginationMargin="24px"
        >
          {services.map((service: Service) => {
            const { name, animated, icon } = service;
            const imageProps = isUnderTablet ? { icon } : { animated };
            return (
              <Slider.Button
                className="service-slide"
                key={uuidv4()}
                onClick={() => {
                  setSelectedService(service);
                  openModal();
                }}
              >
                <ServiceThumbnail name={name} {...imageProps} />
              </Slider.Button>
            );
          })}
        </Slider>
      ) : (
        <ServiceThumbnailsGrid>
          {services.map(({ excerpt, animated, slug, name }) => {
            return (
              <ServiceThumbnail
                name={name}
                animated={animated}
                body={excerpt.excerpt}
                mode="horizontal"
                key={name}
              >
                <div className="link">
                  <Link to={`/services/${slug}`}>
                    <Button size="small" disabled={false}>
                      Read more
                      <FontAwesomeIcon
                        size="sm"
                        className="link__icon"
                        icon={faChevronRight}
                      />
                    </Button>
                  </Link>
                </div>
              </ServiceThumbnail>
            );
          })}
        </ServiceThumbnailsGrid>
      )}

      <SimpleModal
        onClose={closeModal}
        isOpen={modalIsOpen}
        title={selectedService.name}
        image={{
          svg: selectedService.animatedStart.svg,
          title: selectedService.icon.description,
        }}
        imageWidth="180px"
        imageHeight="183px"
        stretch
        center
        content={selectedService.excerpt.excerpt}
        footer={
          <Link to={`/services/${selectedService.slug}`}>
            <Button
              className="modal-btn"
              size="small"
              disabled={false}
              iconRight={faChevronRight}
            >
              Read more
            </Button>
          </Link>
        }
      />
    </StyledServicesSlider>
  );
};
